import { useState, useEffect, useCallback } from "react";
import { Col, Container, Row, Card, CardBody } from "reactstrap";
import { Button, Form } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Select from "react-select";
import config from "../../../../config";
import Loader from "../../../Helper/Loader";
import empty from "is-empty";
import Breadcrumbs from "../../../Helper/Breadcrumbs";
import EditorJsComponent from "./EditorJsComponent";
import FaqEditor from "./FaqEditor";
import { compressImage, validateImageFile } from "../../../Helper/imageCompression";

/* ------------------ HELPERS ------------------ */

/** Format used in UI: DD/MM/YYYY HH:mm:ss + padded trailing digits */
const formatCustomDate = (date) => {
  if (!date) return "";
  const pad = (n) => (n < 10 ? "0" + n : n);
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const millis = date.getMilliseconds().toString().padEnd(3, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}${millis}`;
};

/** Parse UI/custom or ISO string → JS Date */
const parseToDate = (input) => {
  if (!input) return new Date();
  if (input instanceof Date) return input;

  if (typeof input === "string" && input.includes("T")) {
    const d = new Date(input);
    if (!isNaN(d)) return d;
  }

  if (typeof input === "string" && input.includes("/")) {
    try {
      const [datePart, timePart = ""] = input.split(" ");
      const [dd, mm, yyyy] = datePart.split("/").map((s) => parseInt(s, 10));
      if (!dd || !mm || !yyyy) return new Date();

      const [h = 0, m = 0, s = 0] = (timePart.slice(0, 8) || "")
        .split(":")
        .map((n) => parseInt(n, 10));
      const ms = parseInt(timePart.slice(8, 11).padEnd(3, "0"), 10) || 0;

      return new Date(yyyy, mm - 1, dd, h, m, s, ms);
    } catch {
      return new Date();
    }
  }

  const fallback = new Date(input);
  return isNaN(fallback) ? new Date() : fallback;
};

/** JS Date → ISO local string (YYYY-MM-DDTHH:mm:ss.sss) */
const toIsoLocalString = (date) => {
  if (!date) return null;
  const pad = (n, len = 2) => String(n).padStart(len, "0");
  const Y = date.getFullYear();
  const M = pad(date.getMonth() + 1);
  const D = pad(date.getDate());
  const h = pad(date.getHours());
  const m = pad(date.getMinutes());
  const s = pad(date.getSeconds());
  const ms = String(date.getMilliseconds() || 0).padStart(3, "0");
  return `${Y}-${M}-${D}T${h}:${m}:${s}.${ms}`;
};

/** Convert to datetime-local input value */
const toInputDateTimeLocal = (value) => {
  if (!value) return "";
  const d = parseToDate(value);
  if (!d || isNaN(d)) return "";
  const Y = d.getFullYear();
  const M = String(d.getMonth() + 1).padStart(2, "0");
  const D = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${Y}-${M}-${D}T${h}:${m}`;
};

/* ------------------ COMPONENT ------------------ */

const BlogDataForm = (props) => {
  const history = useHistory();
  const propsState = props.history.location.state || {};
  const editMode = propsState.editMode || false;

  const tokenKey = {
    Authorization: "Token " + sessionStorage.getItem("tokenKey"),
    "Content-Type": "multipart/form-data",
  };

  const [form, setForm] = useState({
    title: "",
    slug:"",
    short_content: "",
    content: "",
    author_id: null,
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
    category: null,
    is_popular: false,
    is_featured: false,
    blog_Link: "",
    status: true,
    image: null,
    created_at: "",
    updated_at: "",
    faqs: [],
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState();
  const [categoryList, setCategoryList] = useState([]);
  const [authorList, setAuthorList] = useState([]);
  const [authorOption, setAuthorOption] = useState(null);

  const toastAttr = {
    theme: "colored",
    position: "top-center",
    autoClose: 1000,
    hideProgressBar: true,
  };

  const breadcrumbData = [
    { title: "Seobook", href: "/seobook" },
    { title: "BlogData", href: "/seobook/BlogData" },
    { title: editMode ? "Edit" : "Create", href: "#" },
  ];

  /* ------------------ Fetch Data ------------------ */

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(
          `${config.service}/masters/blog-categories/`,
          { headers: tokenKey }
        );
        setCategoryList(res.data);
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(
          `${config.service}/masters/blog-authors/`,
          { headers: tokenKey }
        );
        setAuthorList(
          res.data.map((a) => ({ value: a.id, label: a.name }))
        );
      } catch (err) {
        console.error("Error fetching authors:", err);
      }
    })();
  }, []);

  // Default created/updated for new
  useEffect(() => {
    if (!editMode) {
      const now = new Date();
      const formatted = formatCustomDate(now);
      setForm((prev) => ({
        ...prev,
        created_at: formatted,
        updated_at: formatted,
      }));
      setLoading(false);
    }
  }, [editMode]);

  // Fetch existing data for edit mode
  useEffect(() => {
    const fetchData = async () => {
      if (editMode && propsState.id) {
        try {
          const resp = await axios.get(
            `${config.service}/masters/blogpost/${propsState.id}`,
            { headers: tokenKey }
          );
          const data = resp.data[0]?.data;
          if (data) {
            setForm({
              ...data,
              author_id: data.author?.id ?? null,
              created_at: formatCustomDate(parseToDate(data.created_at)),
              updated_at: formatCustomDate(parseToDate(data.updated_at)),
            });
            if (data.author)
              setAuthorOption({ value: data.author.id, label: data.author.name });
          }
        } catch (error) {
          console.error("Error fetching data:", error);
          toast.error("Error loading blog data", toastAttr);
        }
      }
      setLoading(false);
    };
    fetchData();
  }, [propsState, editMode]);

  /* ------------------ Handlers ------------------ */

  const handleImageSelect = useCallback(async (selectedFile) => {
    if (!selectedFile) return;

    const { valid, error } = validateImageFile(selectedFile);
    if (!valid) {
      toast.error(error, toastAttr);
      return;
    }

    const optimized = await compressImage(selectedFile);
    setFile(URL.createObjectURL(optimized));
    setForm((prev) => ({ ...prev, image: optimized }));
    setErrors((prev) => ({ ...prev, image: null }));
  }, []);

  const handleInput = useCallback(
    (key, value) => {
      setForm((prev) => ({ ...prev, [key]: value }));

      // live validation for date logic
      if (key === "updated_at") {
        const c = parseToDate(form.created_at);
        const u = parseToDate(value);
        if (u < c)
          setErrors((prev) => ({
            ...prev,
            updated_at: "Updated date cannot be earlier than Created date.",
          }));
        else setErrors((prev) => ({ ...prev, updated_at: null }));
      } else if (errors[key]) {
        setErrors((prev) => ({ ...prev, [key]: null }));
      }
    },
    [errors, form.created_at]
  );

  const handleEditorChange = useCallback(
    (e) => {
      const { name, value, htmlValue } = e.target;
      setForm((prev) => ({ ...prev, [name]: value, content_html: htmlValue }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
    },
    [errors]
  );

  const renderListView = useCallback(() => {
    history.push("/seobook/BlogData");
  }, [history]);

  /* ------------------ Validation ------------------ */

  const validateForm = useCallback(() => {
    const newErrors = {};
    const requiredFields = [
      "title",
      "short_content",
      "content",
      "meta_description",
      "meta_title",
      "meta_keywords",
      "blog_Link",
    ];

    requiredFields.forEach((field) => {
      if (empty(form[field])) newErrors[field] = "This is a required field";
    });

    if (!editMode && empty(form.image))
      newErrors.image = "This is a required field";

    if (empty(form.created_at))
      newErrors.created_at = "Please select created date";
    if (empty(form.updated_at))
      newErrors.updated_at = "Please select updated date";

    // Validate date order
    if (!newErrors.created_at && !newErrors.updated_at) {
      const c = parseToDate(form.created_at);
      const u = parseToDate(form.updated_at);
      if (u < c)
        newErrors.updated_at =
          "Updated date cannot be earlier than Created date.";
    }

    return newErrors;
  }, [form, editMode]);

  /* ------------------ Save ------------------ */

  const saveForm = useCallback(async () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Enter all required details to proceed", toastAttr);
      return;
    }

    setLoading(true);

    const payload = {
      ...form,
      created_at: toIsoLocalString(parseToDate(form.created_at)),
      updated_at: toIsoLocalString(parseToDate(form.updated_at)),
    };

    const url = editMode
      ? `${config.service}/masters/blogpost/${propsState.id}/`
      : `${config.service}/masters/blogpost/`;
    const method = editMode ? "put" : "post";

    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (key === "image" && value instanceof File) {
        formData.append("image", value);
      } else if (key === "faqs") {
        formData.append("faqs", JSON.stringify(value || []));
      } else if (value !== null && value !== undefined) {
        formData.append(
          key,
          typeof value === "boolean" ? (value ? "true" : "false") : value
        );
      }
    });

    try {
      const res = await axios({
        url,
        method,
        headers: tokenKey,
        data: formData,
      });


      if (Array.isArray(res.data)) {
        const fieldErrors = res.data.reduce((acc, item) => {
          if (item.error && typeof item.error === "object") {
            Object.entries(item.error).forEach(([field, messages]) => {
              acc[field] = Array.isArray(messages) ? messages[0] : messages;
            });
          }
          return acc;
        }, {});

        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
          toast.error(Object.values(fieldErrors)[0], toastAttr); // Show first error
          setLoading(false);
          return; // ✅ Stop success flow
        }
      }

      toast.success(
        editMode ? "Updated successfully" : "Created successfully",
        toastAttr
      );
      renderListView();
    } catch (err) {
      console.error("API Error:", err);
      if (err.response?.status === 401) window.location.href = "/";
      else
        toast.error(
          err.response?.data?.message || "Error saving data",
          toastAttr
        );
    } finally {
      setLoading(false);
    }
  }, [form, editMode, propsState, validateForm, renderListView]);

  /* ------------------ Render ------------------ */

  const formFields = [
    { label: "Title *", name: "title", type: "text" },
     { label: "Slug *", name: "slug", type: "text" },
    {
      label: "Short Content *",
      name: "short_content",
      type: "textarea",
      rows: 4,
    },
    { label: "Meta Title *", name: "meta_title", type: "text" },
    {
      label: "Meta Description *",
      name: "meta_description",
      type: "textarea",
      rows: 4,
    },
    {
      label: "Meta Keywords *",
      name: "meta_keywords",
      type: "textarea",
      rows: 3,
    },
    { label: "Blog Link *", name: "blog_Link", type: "text" },
  ];

  return (
    <Container className="vendor">
      <Row>
        <Col md={12} className="contBody">
          <Card>
            <CardBody>
              <div className="card__title pageNav">
                <Breadcrumbs data={breadcrumbData} />
              </div>

              <Row className="formBtnsDiv mb-4">
                <Col md={12} className="d-flex gap-2">
                  <Button variant="primary" onClick={saveForm} className="px-4">
                    {editMode ? "Update" : "Save"}
                  </Button>
                  <Button variant="outline-secondary" onClick={renderListView}>
                    Cancel
                  </Button>
                </Col>
              </Row>

              <Loader loading={loading}>
                <Form className="form defaultForm">
                  {formFields.map((field) => (
                    <Row key={field.name} className="mb-3 align-items-center">
                      <Col md={2}>
                        <Form.Label className="fw-bold">
                          {field.label}
                        </Form.Label>
                      </Col>
                      <Col md={8}>
                        <Form.Control
                          as={field.type === "textarea" ? "textarea" : "input"}
                          type={
                            field.type === "textarea" ? undefined : field.type
                          }
                          rows={field.rows}
                          name={field.name}
                          value={form[field.name] || ""}
                          onChange={(e) =>
                            handleInput(field.name, e.target.value)
                          }
                          isInvalid={!!errors[field.name]}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors[field.name]}
                        </Form.Control.Feedback>
                      </Col>
                    </Row>
                  ))}

                  {/* Image */}
                  <Row className="mb-4">
                    <Col md={2}>
                      <Form.Label className="fw-bold">
                        {editMode ? "Hero Image" : "Hero Image *"}
                      </Form.Label>
                    </Col>
                    <Col md={8}>
                      <Form.Control
                        type="file"
                        name="image"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(e) => {
                          handleImageSelect(e.target.files[0]);
                          e.target.value = "";
                        }}
                        isInvalid={!!errors.image}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.image}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>

                  {(file || form.image) && (
                    <Row className="mb-4">
                      <Col md={{ offset: 2, size: 8 }}>
                        <div className="image-preview-container p-3 border rounded">
                          <p className="text-muted mb-2">Image Preview:</p>
                          <img
                            src={file || form.image}
                            className="img-thumbnail"
                            style={{ maxWidth: "100px", maxHeight: "100px" }}
                            alt="Preview"
                          />
                        </div>
                      </Col>
                    </Row>
                  )}

                  {/* Created At */}
                  <Row className="mb-3 align-items-center">
                    <Col md={2}>
                      <Form.Label className="fw-bold text-secondary">
                        Created At *
                      </Form.Label>
                    </Col>
                    <Col md={8}>
                      <Form.Control
                        type="datetime-local"
                        value={toInputDateTimeLocal(form.created_at)}
                        onChange={(e) => {
                          const dt = new Date(e.target.value);
                          handleInput("created_at", formatCustomDate(dt));
                        }}
                        isInvalid={!!errors.created_at}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.created_at}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>

                  {/* Updated At with "Now" button */}
                  <Row className="mb-3 align-items-center">
                    <Col md={2}>
                      <Form.Label className="fw-bold text-secondary">
                        Updated At *
                      </Form.Label>
                    </Col>
                    <Col md={6}>
                      <Form.Control
                        type="datetime-local"
                        value={toInputDateTimeLocal(form.updated_at)}
                        onChange={(e) => {
                          const dt = new Date(e.target.value);
                          handleInput("updated_at", formatCustomDate(dt));
                        }}
                        isInvalid={!!errors.updated_at}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.updated_at}
                      </Form.Control.Feedback>
                    </Col>
                    {editMode && (
                      <Col md={2}>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => {
                            const now = new Date();
                            handleInput("updated_at", formatCustomDate(now));
                            toast.info(
                              "Updated time set to current",
                              toastAttr
                            );
                          }}
                        >
                          Now
                        </Button>
                      </Col>
                    )}
                  </Row>

                  {/* Content */}
                  <Row className="mb-4">
                    <Col md={2}>
                      <Form.Label className="fw-bold">Content *</Form.Label>
                    </Col>
                    <Col md={10}>
                      <EditorJsComponent
                        name="content"
                        data={form.content}
                        onChange={handleEditorChange}
                        placeholder="Write your blog content here..."
                      />
                      {errors.content && (
                        <div className="invalid-feedback d-block">
                          {errors.content}
                        </div>
                      )}
                    </Col>
                  </Row>

                  {/* FAQs */}
                  <Row className="mb-4">
                    <Col md={2}>
                      <Form.Label className="fw-bold">FAQs</Form.Label>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        Shown as an accordion on the blog page, and included
                        as FAQ structured data for SEO.
                      </div>
                    </Col>
                    <Col md={10}>
                      <FaqEditor
                        faqs={form.faqs}
                        onChange={(faqs) => handleInput("faqs", faqs)}
                      />
                    </Col>
                  </Row>

                  {/* Category */}
                  <Row className="align-items-center mb-3">
                    <Col md={2}>
                      <Form.Label className="fw-bold text-secondary">
                        Category
                      </Form.Label>
                    </Col>
                    <Col md={8}>
                      <Form.Select
                        name="category"
                        style={{
                          padding: "6px",
                        }}
                        value={form.category || ""}
                        onChange={(e) =>
                          handleInput("category", e.target.value)
                        }
                      >
                        <option value="">Select Category</option>
                        {categoryList.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Row>

                  {/* Author */}
                  <Row className="align-items-center mb-3">
                    <Col md={2}>
                      <Form.Label className="fw-bold text-secondary">
                        Author
                      </Form.Label>
                    </Col>
                    <Col md={8}>
                      <Select
                        className={`selectBox ${errors.author_id ? "inValidField" : ""}`}
                        value={authorOption}
                        onChange={(option) => {
                          setAuthorOption(option);
                          handleInput("author_id", option ? option.value : null);
                        }}
                        options={authorList}
                        placeholder="Search and select an author..."
                        isClearable
                      />
                      {errors.author_id && (
                        <Form.Control.Feedback type="invalid" className="d-block">
                          {errors.author_id}
                        </Form.Control.Feedback>
                      )}
                    </Col>
                  </Row>

                  {/* Switches */}
                  {["status", "is_popular", "is_featured"].map((key) => (
                    <Row className="mb-3" key={key}>
                      <Col md={12}>
                        <Row>
                          <Col md={2}>
                            <Form.Label
                              id={key}
                              className="fw-bold text-capitalize"
                            >
                              {key.replace("_", " ")}
                            </Form.Label>
                          </Col>
                          <Col md={1}>
                            <Form.Check
                              type="switch"
                              id={key}
                              checked={form[key]}
                              onChange={(e) =>
                                handleInput(key, e.target.checked)
                              }
                            />
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  ))}
                </Form>
              </Loader>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default BlogDataForm;
